// TradingCommand.js - BRD Design System Applied
// Design tokens: #08101E bg, #0C1525 surface, DM Mono, BRD color vocabulary
// Socket → Raw State → Derived State (useMemo) → Components

import socketIOClient from "socket.io-client";
import config from "./config";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";

// Services
import { prepareData } from "./service/PredictionAccuracy";
import prepareOrder from "./service/PrepareOrder";
import buildActivities from "./service/BuildActivity";
import getDataAPI from "./service/apiservice";
import { getToken } from "./service/AuthService";

// Components
import ProfessionalCountdownTimer from "./components/timer/ProfessionalCountdownTimer";
import FeedCard from "./components/feedcard/Feedcard";
import MarketChart from "./components/TradingCommand/MarketChart";
import EvaluationTimeline from "./components/TradingCommand/EvaluationTimeline";
import EvaluationDetailModal from "./components/TradingCommand/EvaluationDetailModal";
import PositionDetailModal from "./components/TradingCommand/PositionDetailModal";
import DayPnLSummary from "./components/TradingCommand/DayPnLSummary";
import ActivePositions from "./components/TradingCommand/ActivePositions";
import ClosedPositions from "./components/TradingCommand/ClosedPositions";
import Orders from "./components/TradingCommand/Orders";
import PositionLedger from './components/PositionLedger/PositionLedger';
import OrderLedgerBuilder from './components/PositionLedger/OrderLedgerBuilder';
import OptionsStrategyPanel from "./components/TradingCommand/OptionsStrategyPanel";
import BotIntelligencePanel from "./components/TradingCommand/BotIntelligencePanel";
import MemoryConfidenceBar from "./components/TradingCommand/MemoryConfidenceBar";
import RegimeDecisionTally from "./components/RegimeDecisionTally";
import ModelConsensusPanel from "./components/ModelConsensusPanel";
// Redux
import { useDispatch } from "react-redux";
import {
  addOrder,
  addBars,
  addActivity,
  addPrediction,
  clearOrder,
  clearBars,
  clearActivity,
  clearPrediction,
} from "./service/actions";

const ENDPOINT = config.API_SOCKET_URL;

// ============================================================================
// BRD DESIGN TOKENS
// ============================================================================
const T = {
  bg:     "#08101E",
  bg1:    "#0C1525",
  bg2:    "#101B2E",
  border: "#1E2D42",
  text:   "#E8EDF5",
  body:   "#B8C8DC",
  meta:   "#6A7D95",
  green:  "#00E896",
  cyan:   "#00C8FF",
  amber:  "#FFB340",
  red:    "#FF4466",
  purple: "#9966FF",
  blue:   "#4488FF",
};

// ============================================================================
// SHARED PRIMITIVE COMPONENTS (BRD Section 6.3)
// ============================================================================

// Eyebrow — uppercase 9px DM Mono label
const Eyebrow = ({ children, color = T.meta }) => (
  <div style={{
    fontFamily: "'DM Mono', monospace",
    fontSize: "9px",
    fontWeight: 500,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color,
    marginBottom: "4px",
  }}>
    {children}
  </div>
);

// Chip — small inline badge with background tint
const Chip = ({ label, color = T.cyan }) => {
  const bg = color + "18";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      borderRadius: "4px",
      backgroundColor: bg,
      border: `1px solid ${color}40`,
      fontFamily: "'DM Mono', monospace",
      fontSize: "10px",
      fontWeight: 500,
      letterSpacing: "0.08em",
      color,
    }}>
      {label}
    </span>
  );
};

// Orb — pulsing status indicator
const Orb = ({ color = T.green, size = 8 }) => (
  <span style={{
    display: "inline-block",
    width: size,
    height: size,
    borderRadius: "50%",
    backgroundColor: color,
    boxShadow: `0 0 6px ${color}`,
    animation: "orb-pulse 2s ease-in-out infinite",
    flexShrink: 0,
  }} />
);

// StatCard — animated DM Mono stat display
const StatCard = ({ eyebrow, value, subLabel, color = T.green, prefix = "" }) => (
  <div style={{
    backgroundColor: T.bg1,
    border: `1px solid ${T.border}`,
    borderRadius: "6px",
    padding: "10px 14px",
    minWidth: 0,
  }}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <div style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "20px",
      fontWeight: 600,
      color,
      lineHeight: 1.1,
    }}>
      {prefix}{value}
    </div>
    {subLabel && (
      <div style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "10px",
        color: T.meta,
        marginTop: "3px",
      }}>
        {subLabel}
      </div>
    )}
  </div>
);

// ============================================================================
// HEADER COMPONENT — BRD-styled
// ============================================================================
const TradingHeader = ({ symbol, close, marketStatus, chartTS }) => {
  const isOpen = marketStatus === "open";
  const statusColor = isOpen ? T.green : T.amber;

  return (
    <div style={{
      backgroundColor: T.bg1,
      border: `1px solid ${T.border}`,
      borderRadius: "8px",
      padding: "10px 20px",
      marginBottom: "12px",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>

        {/* LEFT — Symbol + Price */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div>
            <Eyebrow color={T.cyan}>Instrument</Eyebrow>
            <div style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "22px",
              fontWeight: 600,
              color: T.text,
              lineHeight: 1,
            }}>
              {symbol || "—"}
              <span style={{ color: T.green, marginLeft: "10px" }}>
                ${close || "—"}
              </span>
            </div>
          </div>

          {/* Market status chip */}
          <div style={{ paddingTop: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Orb color={statusColor} />
              <span style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px",
                color: statusColor,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}>
                Market {marketStatus || "—"}
              </span>
            </div>
          </div>
        </div>

        {/* CENTER — Countdown Timer */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <ProfessionalCountdownTimer />
        </div>

        {/* RIGHT — Timestamp + BerBul label */}
        <div style={{ textAlign: "right" }}>
          <Eyebrow>Last Update</Eyebrow>
          <div style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px",
            color: T.body,
          }}>
            {chartTS || "—"}
          </div>
          <div style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px",
            color: T.meta,
            marginTop: "2px",
            letterSpacing: "0.1em",
          }}>
            BЕRBUL AI · LAYER 01
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SECTION WRAPPER — consistent card chrome
// ============================================================================
const Section = ({ eyebrow, children, style = {} }) => (
  <div style={{
    backgroundColor: T.bg1,
    border: `1px solid ${T.border}`,
    borderRadius: "8px",
    padding: "16px",
    ...style,
  }}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    {children}
  </div>
);

// ============================================================================
// CONNECTING SCREEN
// ============================================================================
const ConnectingScreen = () => (
  <div style={{
    backgroundColor: T.bg1,
    border: `1px solid ${T.border}`,
    borderRadius: "8px",
    padding: "60px 20px",
    textAlign: "center",
  }}>
    <div style={{ marginBottom: "16px" }}>
      <Orb color={T.cyan} size={12} />
    </div>
    <div style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "14px",
      color: T.body,
      letterSpacing: "0.06em",
    }}>
      Connecting to trading system…
    </div>
    <div style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "10px",
      color: T.meta,
      marginTop: "6px",
    }}>
      Awaiting socket handshake
    </div>
  </div>
);

// ============================================================================
// ERROR BANNER
// ============================================================================
const ErrorBanner = ({ message }) => (
  <div style={{
    backgroundColor: T.red + "18",
    border: `1px solid ${T.red}40`,
    borderRadius: "6px",
    padding: "10px 16px",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }}>
    <span style={{ color: T.red, fontSize: "14px" }}>⚠</span>
    <span style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "12px",
      color: T.red,
    }}>
      {message}
    </span>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const TradingCommand = () => {
  // -----------------------------------------------------------------------
  // RAW STATE (From Socket)
  // -----------------------------------------------------------------------
  const [liveBars, setLiveBars] = useState([]);
  const [rawOrders, setRawOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);

  // -----------------------------------------------------------------------
  // UI STATE
  // -----------------------------------------------------------------------
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptionEval, setSelectedOptionEval] = useState(null);

  // -----------------------------------------------------------------------
  // METADATA
  // -----------------------------------------------------------------------
  const [marketInfo, setMarketInfo] = useState({
    symbol: "",
    close: "",
    status: "",
    timestamp: "",
    isActive: false,
    error: null,
  });

  // -----------------------------------------------------------------------
  // REFS
  // -----------------------------------------------------------------------
  const prevOrderKeyRef = useRef("");
  const prevHistoryLengthRef = useRef(0);
  const allOrdersRef = useRef([]);

  const dispatch = useDispatch();

  // =======================================================================
  // DERIVED STATE (useMemo)
  // =======================================================================

  const activities = useMemo(() => {
    if (!rawOrders || rawOrders.length === 0) return [];
    try {
      const activityArray = [];
      rawOrders.forEach((row) => {
        const [activity] = prepareOrder(row);
        if (activity) activityArray.push(activity);
      });
      if (activityArray.length === 0) return [];
      return buildActivities(activityArray);
    } catch (err) {
      console.error("Activity processing error:", err);
      return [];
    }
  }, [rawOrders]);

  const positions = useMemo(() => {
    if (!activities || activities.length === 0) return [];

    const tradeGroups = {};
    activities.forEach((activity) => {
      const groupId = activity.group;
      if (!tradeGroups[groupId]) tradeGroups[groupId] = [];
      tradeGroups[groupId].push(activity);
    });

    const allPositions = [];

    Object.keys(tradeGroups).forEach((groupId) => {
      const groupActivities = tradeGroups[groupId].sort(
        (a, b) => new Date(a.created_at_gmt) - new Date(b.created_at_gmt)
      );

      const firstActivity = groupActivities[0];
      const lastActivity = groupActivities[groupActivities.length - 1];
      const openQty = lastActivity.open_qty || 0;

      const isLong = firstActivity.side === "buy";
      const entryPrice = parseFloat(firstActivity.price);
      const qty = Math.abs(firstActivity.qty);

      let target = 0;
      let stop = 0;

      rawOrders.forEach((orderRow) => {
        const order = orderRow.order || orderRow;
        if (order.symbol === firstActivity.symbol) {
          const orderTime = new Date(order.created_at);
          const entryTime = new Date(firstActivity.created_at_gmt);
          const timeDiff = Math.abs(orderTime - entryTime);
          if (timeDiff < 60000) {
            const limitPrice = order.limit_price ? parseFloat(order.limit_price) : 0;
            const stopPrice = order.stop_price ? parseFloat(order.stop_price) : 0;
            if (order.type === "limit" && order.side !== firstActivity.side && limitPrice > 0) {
              if ((isLong && limitPrice > entryPrice) || (!isLong && limitPrice < entryPrice)) {
                if (target === 0) target = limitPrice;
              }
            }
            if ((order.type === "stop" || order.type === "stop_limit") && stopPrice > 0) {
              if ((isLong && stopPrice < entryPrice) || (!isLong && stopPrice > entryPrice)) {
                if (stop === 0) stop = stopPrice;
              }
            }
          }
        }
      });

      if (openQty !== 0) {
        const unrealizedPL = currentPrice > 0
          ? isLong ? (currentPrice - entryPrice) * qty : (entryPrice - currentPrice) * qty
          : 0;
        const riskAmount = stop > 0 ? Math.abs(entryPrice - stop) * qty : 0;
        const rewardAmount = target > 0 ? Math.abs(target - entryPrice) * qty : 0;
        const rrRatio = riskAmount > 0 ? (rewardAmount / riskAmount).toFixed(2) : 0;
        const entryTime = new Date(firstActivity.created_at_gmt);
        const holdTimeMin = Math.floor((new Date() - entryTime) / 60000);

        allPositions.push({
          ...lastActivity,
          id: lastActivity.id || lastActivity.a_id,
          a_id: lastActivity.a_id,
          group: groupId,
          symbol: firstActivity.symbol,
          isLong,
          side: isLong ? "buy" : "sell",
          entryPrice,
          currentPrice,
          qty,
          open_qty: openQty,
          target,
          stop,
          tp: target,
          sl: stop,
          unrealizedPL,
          riskAmount,
          rrRatio,
          entryTime: firstActivity.created_at_gmt,
          exitTime: null,
          exitPrice: null,
          holdTimeMin,
          isWin: false,
          exitType: null,
        });
      } else {
        const exitPrice = parseFloat(lastActivity.price);
        const realizedPL = parseFloat(lastActivity.pl_delta) || 0;
        const tax = parseFloat(lastActivity.tax) || 0;
        const isWin = realizedPL > 0;
        const entryTime = new Date(firstActivity.created_at_gmt);
        const exitTimeDate = new Date(lastActivity.submitted_at_gmt);
        const holdTimeMin = Math.floor((exitTimeDate - entryTime) / 60000);
        const activityType = lastActivity.type;
        let exitType = "Unknown";
        if (activityType === "stop") exitType = "Stop Hit";
        else if (activityType === "limit" && lastActivity.side !== firstActivity.side) exitType = "Target Fill";
        else if (activityType === "market") exitType = "Market Close";
        else if (lastActivity.side === "sell" && firstActivity.side === "buy") exitType = "Target Fill";
        else if (lastActivity.side === "buy" && firstActivity.side === "sell") exitType = "Cover";
        else exitType = "Market Close";

        allPositions.push({
          ...lastActivity,
          id: lastActivity.id || lastActivity.a_id,
          a_id: lastActivity.a_id,
          group: groupId,
          symbol: firstActivity.symbol,
          isLong,
          side: isLong ? "buy" : "sell",
          entryPrice,
          exitPrice,
          currentPrice: exitPrice,
          qty,
          open_qty: openQty,
          target,
          stop,
          tp: target,
          sl: stop,
          unrealizedPL: realizedPL,
          tax,
          riskAmount: 0,
          rrRatio: 0,
          entryTime: firstActivity.created_at_gmt,
          exitTime: lastActivity.submitted_at_gmt,
          holdTimeMin,
          isWin,
          exitType,
        });
      }
    });

    return allPositions;
  }, [activities, rawOrders, currentPrice]);
  // Ledger data — single source of truth for position state.
  // Uses OrderLedgerBuilder directly with allOrders (full order history incl. options).
  // Fresh builder per call avoids React StrictMode double-invocation mutation bugs.
  const ledgerData = useMemo(() => {
    if (!allOrders || allOrders.length === 0) {
      return { stocks: [], options: [], summary: {} };
    }
    try {
      const builder = new OrderLedgerBuilder();
      return builder.processOrderHistory(allOrders);
    } catch (err) {
      console.error('LedgerBuilder error:', err);
      return { stocks: [], options: [], summary: {} };
    }
  }, [allOrders]);

  // Adapter: map OrderLedgerBuilder lot fields to the shape ActivePositions/PositionCard expect
  const adaptLot = (lot) => {
    const isLong = lot.direction === 'LONG';
    const entryPrice = parseFloat(lot.entry_fill_price || lot.avg_entry_price || 0);
    const qty = parseFloat(lot.quantity || lot.qty_open || 0);
    const entryTimeRaw = lot.entry_submitted_at_raw || lot.entry_submitted_at || lot.entry_filled_at || null;
    const entryTimeMs = entryTimeRaw ? new Date(entryTimeRaw).getTime() : 0;
    const holdTimeMin = entryTimeMs ? Math.floor((Date.now() - entryTimeMs) / 60000) : 0;
    const unrealizedPL = currentPrice > 0
      ? isLong ? (currentPrice - entryPrice) * qty : (entryPrice - currentPrice) * qty
      : 0;
    const target = parseFloat(lot.tp_limit) || 0;
    const stop   = parseFloat(lot.sl_stop)  || 0;
    const riskAmount   = stop   > 0 ? Math.abs(entryPrice - stop)   * qty : 0;
    const rewardAmount = target > 0 ? Math.abs(target - entryPrice) * qty : 0;
    const rrRatio = riskAmount > 0 ? (rewardAmount / riskAmount).toFixed(2) : 0;
    return {
      // Identity — order_id added to createLot so callbacks can resolve position
      id:          lot.order_id || lot.symbol + '_' + entryTimeMs,
      order_id:    lot.order_id || null,
      symbol:      lot.symbol,
      isLong,
      side:        isLong ? 'buy' : 'sell',
      qty,
      open_qty:    qty,
      entryPrice,
      currentPrice,
      price:       entryPrice,
      target,
      stop,
      tp:          target,
      sl:          stop,
      tp_limit:    lot.tp_limit,
      sl_stop:     lot.sl_stop,
      sl_limit:    lot.sl_limit,
      rr:          lot.rr,
      unrealizedPL,
      riskAmount,
      rrRatio,
      entryTime:   entryTimeRaw,
      exitTime:    null,
      exitPrice:   null,
      holdTimeMin,
      isWin:       false,
      exitType:    null,
    };
  };

  // Active positions sourced from OrderLedgerBuilder — FIFO inventory is the ground truth.
  // The activities-based pipeline (BuildActivity) has cumulative counter bugs that
  // cause closed positions to appear open. Ledger lots with qty_open > 0 are reliable.
  const activePositions = useMemo(
    () => (ledgerData.stocks || [])
      .filter(p => p.status === 'active')
      .map(adaptLot),
    [ledgerData, currentPrice]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const closedPositions = useMemo(
    () => positions.filter(p => (p.open_qty ?? 0) === 0 && p.exitTime && p.exitPrice),
    [positions]
  );

  const metrics = useMemo(() => {
    // --- UNREALIZED: live calculation from active positions (currentPrice-driven) ---
    const totalUnrealizedPL = activePositions.reduce(
      (sum, pos) => sum + (parseFloat(pos.unrealizedPL) || 0), 0
    );

    // --- REALIZED: sourced from OrderLedger closed rows (FIFO-correct P&L) ---
    const closedStocks = (ledgerData.stocks || []).filter(p => p.status === 'closed');
    const totalRealizedPL = closedStocks.reduce(
      (sum, p) => sum + (parseFloat(p.total_pnl_num) || 0), 0
    );

    const dayPL = totalRealizedPL + totalUnrealizedPL;

    // Win/loss: a trade is a win if realized P&L > 0
    const wins = closedStocks.filter(p => (parseFloat(p.total_pnl_num) || 0) > 0).length;
    const closedCount = closedStocks.length;
    const winRate = closedCount > 0
      ? parseFloat(((wins / closedCount) * 100).toFixed(0))
      : 0;

    // Hold time stats — both avg and median from ledger hold_time_seconds
    const holdTimes = closedStocks
      .map(p => parseFloat(p.hold_time_seconds) || 0)
      .filter(s => s > 0)
      .sort((a, b) => a - b);
    const avgHoldTime = holdTimes.length > 0
      ? Math.round(holdTimes.reduce((s, v) => s + v, 0) / holdTimes.length / 60)
      : 0;
    const medianHoldTime = holdTimes.length > 0
      ? (() => {
          const mid = Math.floor(holdTimes.length / 2);
          const medianSecs = holdTimes.length % 2 !== 0
            ? holdTimes[mid]
            : (holdTimes[mid - 1] + holdTimes[mid]) / 2;
          return Math.round(medianSecs / 60);
        })()
      : 0;

    const pnls = closedStocks.map(p => parseFloat(p.total_pnl_num) || 0);
    const bestTrade  = pnls.length > 0 ? Math.max(...pnls) : 0;
    const worstTrade = pnls.length > 0 ? Math.min(...pnls) : 0;

    // Fees: ledger doesn't track fees — keep from activities pipeline as fallback
    const totalFees = closedPositions.reduce(
      (sum, p) => sum + Math.abs(parseFloat(p.tax) || 0), 0
    );

    return {
      dayPL:           dayPL           || 0,
      realizedPL:      totalRealizedPL || 0,
      unrealizedPL:    totalUnrealizedPL || 0,
      activePositions: activePositions.length || 0,
      closedTrades:    closedCount     || 0,
      winRate:         winRate         || 0,
      avgHoldTime:     avgHoldTime     || 0,
      medianHoldTime:  medianHoldTime  || 0,
      bestTrade:       bestTrade       || 0,
      worstTrade:      worstTrade      || 0,
      totalFees:       totalFees       || 0,
    };
  }, [activePositions, ledgerData, closedPositions]);

  const proceedEvals = useMemo(() => {
    return [...(liveBars || [])]
      .filter(b => (b.eval_decision || b.decision) === 'PROCEED')
      .sort((a, b) => new Date(b.t || 0) - new Date(a.t || 0));
  }, [liveBars]);


  // =======================================================================
  // HELPER FUNCTIONS
  // =======================================================================

  const filterChartDataForPosition = useCallback((bars, position) => {
    if (!bars || bars.length === 0 || !position) return [];
    const entryTime = new Date(position.entryTime);
    const exitTime = position.exitTime ? new Date(position.exitTime) : new Date(Date.now() + 86400000);
    return bars.filter((bar) => {
      const barTime = new Date(bar.t);
      return barTime >= entryTime && barTime <= exitTime;
    });
  }, []);

  const filterEvaluationsForPosition = useCallback((bars, position) => {
    if (!bars || bars.length === 0 || !position) return [];
    const evaluations = [];
    bars.forEach((bar, index) => {
      const decision = bar.eval_decision || bar.decision || bar.signal;
      if (decision) {
        evaluations.push({ ...bar, index });
      }
    });
    return evaluations;
  }, []);

  const handleClosePosition = useCallback((positionId) => {
    getDataAPI(positionId, "closePosition")
      .then((output) => {
        console.log("Close position response:", output);
        alert(`Position closed: ${output}`);
      })
      .catch((err) => {
        console.error("Error closing position:", err);
        setMarketInfo((prev) => ({ ...prev, error: "Failed to close position. Please try again." }));
      });
  }, []);

  const handlePositionSelect = useCallback((position) => {
    if (!position) { setShowPositionModal(false); return; }
    setSelectedPosition(position);
    setShowPositionModal(true);
  }, []);

  const handlePositionModalClose = useCallback(() => {
    setShowPositionModal(false);
    setSelectedPosition(null);
  }, []);

  const handleEvaluationSelect = useCallback((evaluation) => {
    setSelectedEvaluation(evaluation);
    setShowEvalModal(true);
  }, []);

  const handleViewFullStory = useCallback((position) => {
    setSelectedPosition(position);
    setShowPositionModal(false);
  }, []);

  const handleOptionClick = useCallback((position, matchedEval) => {
    setSelectedOption(position);
    setSelectedOptionEval(matchedEval);
    setShowOptionsModal(true);
  }, []);

  // =======================================================================
  // SOCKET & DATA EFFECTS
  // =======================================================================

  useEffect(() => {
    const socket = socketIOClient(ENDPOINT, {
      transports: ["websocket", "polling", "flashsocket"],
      transportOption: {
        websocket: { extraHeaders: { Authorization: `Bearer ${getToken()}` } },
      },
      reconnectionDelay: 1000,
      reconnection: true,
      reconnectionAttempts: 3,
      agent: false,
      upgrade: false,
      rejectUnauthorized: false,
      extraHeaders: { Authorization: `Bearer ${getToken()}` },
    });

    socket.on("connect_error", (err) => {
      setMarketInfo((prev) => ({ ...prev, error: "Live connections only during active trading hours. Please wait for market to open..." }));
      console.error("Socket connection error:", err);
    });

    socket.on("predictionHistoryEvent", (data) => {
      try {
        const rawHistory = JSON.parse(data["data"]);
        const marketStatus = data["market_status"];

        const preparedData = rawHistory.map((rawBar) => {
          const processedBar = prepareData(rawBar);
          let evalAgentStory = rawBar.eval_agent_story;
          if (typeof evalAgentStory === 'string') {
            try { evalAgentStory = JSON.parse(evalAgentStory); } catch { evalAgentStory = null; }
          }
          return {
            ...processedBar,
            eval_decision: rawBar.eval_decision,
            eval_type: rawBar.eval_type,
            eval_reasoning: rawBar.eval_reasoning,
            eval_narrative_summary: rawBar.eval_narrative_summary,
            eval_agent_story: evalAgentStory,
            eval_result: rawBar.eval_result,
            eval_position_id: rawBar.eval_position_id,
            eval_new_stop_loss: rawBar.eval_new_stop_loss,
            eval_new_target: rawBar.eval_new_target,
            eval_pattern_influence: rawBar.eval_pattern_influence,
            eval_adjustment_applied: rawBar.eval_adjustment_applied,
            eval_execution_time: rawBar.eval_execution_time,
            eval_error: rawBar.eval_error,
            position_id: rawBar.position_id || rawBar.eval_position_id,
            decision: rawBar.decision || rawBar.eval_decision || rawBar.signal,
          };
        });

        const slidingData = preparedData.slice(-360);
        setLiveBars(slidingData);

        if (slidingData.length > 0) {
          const lastData = slidingData[slidingData.length - 1];
          setMarketInfo({
            symbol: lastData.symbol,
            close: lastData.close,
            status: marketStatus,
            timestamp: new Date(lastData.t).toLocaleString("en-US"),
            isActive: true,
            error: null,
          });
          setCurrentPrice(parseFloat(lastData.close) || 0);
        }

        dispatch(clearBars());
        dispatch(addBars(slidingData));
        dispatch(clearPrediction());
        dispatch(addPrediction(slidingData));
      } catch (err) {
        console.error("Prediction history error:", err);
        setMarketInfo((prev) => ({ ...prev, error: "Error processing prediction data" }));
      }
    });

    socket.on("orderHistoryEvent", (data) => {
      try {
        const allOrdersData = JSON.parse(data["data"]);
        setAllOrders(allOrdersData);
        const equityOrders = allOrdersData.filter(o => o.order.asset_class === "us_equity");

        if (allOrdersRef.current.length === 0 && equityOrders.length > 0) {
          allOrdersRef.current = equityOrders;
          setRawOrders([...equityOrders]);
          return;
        }

        if (equityOrders.length > allOrdersRef.current.length) {
          allOrdersRef.current = equityOrders;
          setRawOrders([...equityOrders]);
          return;
        }

        const existingOrderIds = new Set(allOrdersRef.current.map((o) => o.order?.id).filter(Boolean));
        const newOrders = equityOrders.filter((o) => !existingOrderIds.has(o.order?.id));

        if (newOrders.length > 0) {
          allOrdersRef.current = [...allOrdersRef.current, ...newOrders];
          setRawOrders([...allOrdersRef.current]);
        }
      } catch (err) {
        console.error("Order history error:", err);
      }
    });

    socket.on("tradeEvent", (data) => {
      try {
        const tradeBar = data["data"] ? JSON.parse(data["data"]) : {};
        if (tradeBar && tradeBar.t) {
          if (typeof tradeBar.eval_agent_story === 'string') {
            try { tradeBar.eval_agent_story = JSON.parse(tradeBar.eval_agent_story); } catch { tradeBar.eval_agent_story = null; }
          }

          let dt = new Date(tradeBar.t);
          dt.setSeconds(0);
          dt.setMilliseconds(0);
          tradeBar.t = dt.toISOString().replace(".000Z", ".000000000Z");

          setCurrentPrice(parseFloat(tradeBar.close) || 0);

          setLiveBars((currentBars) => {
            if (currentBars.length === 0 || tradeBar.t > currentBars[currentBars.length - 1].t) {
              return [...currentBars, tradeBar].slice(-360);
            } else if (tradeBar.t === currentBars[currentBars.length - 1].t) {
              const newBars = [...currentBars];
              newBars.pop();
              newBars.push(tradeBar);
              return newBars.slice(-360);
            }
            return currentBars;
          });

          setMarketInfo((prev) => ({
            ...prev,
            close: tradeBar.close,
            symbol: tradeBar.symbol,
            timestamp: new Date(tradeBar.t).toLocaleString("en-US"),
            isActive: true,
          }));
        }
      } catch (err) {
        console.error("Trade event error:", err);
      }
    });

    socket.emit("getPredictionHistory", null);
    socket.emit("getOrderHistory", null);

    return () => { socket.disconnect(); };
  }, [dispatch]);

  useEffect(() => {
    if (activities.length > 0) {
      dispatch(clearActivity());
      dispatch(addActivity(activities));
    }
  }, [activities, dispatch]);

  useEffect(() => {
    if (rawOrders.length > 0) {
      dispatch(clearOrder());
      dispatch(addOrder(rawOrders));
    }
  }, [rawOrders, dispatch]);

  // =======================================================================
  // RENDER
  // =======================================================================

  return (
    <div style={{ backgroundColor: T.bg, minHeight: "100vh", fontFamily: "'DM Mono', monospace" }}>
      {/* ------------------------------------------------------------------ */}
      {/* GLOBAL STYLES — DM Mono font + Orb animation + BRD token overrides */}
      {/* ------------------------------------------------------------------ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');

        body {
          background-color: ${T.bg} !important;
          color: ${T.text} !important;
          font-family: 'DM Mono', monospace !important;
        }

        /* Override Bootstrap cards with BRD surface tokens */
        .card {
          background-color: ${T.bg1} !important;
          border: 1px solid ${T.border} !important;
          color: ${T.text} !important;
          border-radius: 8px !important;
        }
        .card-body { background-color: ${T.bg1} !important; color: ${T.text} !important; }
        .card-header {
          background-color: ${T.bg1} !important;
          border-bottom: 1px solid ${T.border} !important;
          color: ${T.text} !important;
          font-family: 'DM Mono', monospace !important;
        }

        /* Bootstrap table overrides */
        .table { color: ${T.text} !important; font-family: 'DM Mono', monospace !important; }
        .table td, .table th { border-color: ${T.border} !important; }
        .table-dark { background-color: ${T.bg1} !important; }
        .table thead th { color: ${T.meta} !important; font-size: 10px !important; letter-spacing: 0.08em !important; text-transform: uppercase !important; }
        .table tbody tr:hover { background-color: ${T.bg2} !important; }

        /* Alert override */
        .alert-danger {
          background-color: ${T.red}18 !important;
          border-color: ${T.red}40 !important;
          color: ${T.red} !important;
        }

        /* Bootstrap utility class overrides */
        .text-success { color: ${T.green} !important; }
        .text-danger  { color: ${T.red} !important; }
        .text-warning { color: ${T.amber} !important; }
        .text-info    { color: ${T.cyan} !important; }
        .text-muted   { color: ${T.meta} !important; }
        .text-white   { color: ${T.text} !important; }

        /* Spinner */
        .spinner-border { color: ${T.cyan} !important; }

        /* Modal overrides */
        .modal-content {
          background-color: ${T.bg1} !important;
          border: 1px solid ${T.border} !important;
          color: ${T.text} !important;
          font-family: 'DM Mono', monospace !important;
        }
        .modal-header {
          border-bottom: 1px solid ${T.border} !important;
          background-color: ${T.bg1} !important;
        }
        .modal-footer { border-top: 1px solid ${T.border} !important; }
        .modal-backdrop { background-color: ${T.bg} !important; }

        /* Badge/Chip overrides */
        .badge { font-family: 'DM Mono', monospace !important; }

        /* Input overrides */
        .form-control {
          background-color: ${T.bg2} !important;
          border-color: ${T.border} !important;
          color: ${T.text} !important;
          font-family: 'DM Mono', monospace !important;
        }
        .form-control:focus {
          border-color: ${T.cyan} !important;
          box-shadow: 0 0 0 2px ${T.cyan}20 !important;
        }

        /* Button overrides */
        .btn-primary {
          background-color: ${T.cyan}22 !important;
          border-color: ${T.cyan}60 !important;
          color: ${T.cyan} !important;
          font-family: 'DM Mono', monospace !important;
        }
        .btn-primary:hover {
          background-color: ${T.cyan}40 !important;
          border-color: ${T.cyan} !important;
        }
        .btn-danger {
          background-color: ${T.red}22 !important;
          border-color: ${T.red}60 !important;
          color: ${T.red} !important;
        }
        .btn-success {
          background-color: ${T.green}22 !important;
          border-color: ${T.green}60 !important;
          color: ${T.green} !important;
        }
        .btn-secondary {
          background-color: ${T.bg2} !important;
          border-color: ${T.border} !important;
          color: ${T.body} !important;
        }

        /* Nav tabs */
        .nav-tabs { border-bottom-color: ${T.border} !important; }
        .nav-tabs .nav-link {
          color: ${T.meta} !important;
          font-family: 'DM Mono', monospace !important;
          font-size: 11px !important;
          letter-spacing: 0.06em !important;
          border-color: transparent !important;
        }
        .nav-tabs .nav-link.active {
          background-color: ${T.bg1} !important;
          border-color: ${T.border} ${T.border} ${T.bg1} !important;
          color: ${T.cyan} !important;
        }
        .nav-tabs .nav-link:hover { color: ${T.body} !important; border-color: ${T.border} !important; }

        /* Scrollbar */
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${T.bg}; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${T.meta}; }

        /* Orb pulse animation */
        @keyframes orb-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 6px currentColor; }
          50% { opacity: 0.6; box-shadow: 0 0 12px currentColor; }
        }

        /* Row divider */
        hr { border-color: ${T.border} !important; opacity: 1 !important; }

        /* Small header text inside child components */
        h1, h2, h3, h4, h5, h6 {
          font-family: 'DM Mono', monospace !important;
          color: ${T.text} !important;
        }

        p, span, small, label {
          font-family: 'DM Mono', monospace;
        }

        /* PositionLedger tab stripe */
        .position-ledger-header,
        .position-ledger-tabs { background-color: ${T.bg1} !important; }

        /* EvaluationTimeline row highlights */
        .eval-proceed { background-color: ${T.green}10 !important; }
        .eval-skip    { background-color: ${T.red}10 !important; }
      `}</style>

      <Container fluid className="py-3" style={{ maxWidth: "1800px" }}>

        {/* ---------------------------------------------------------------- */}
        {/* HEADER                                                           */}
        {/* ---------------------------------------------------------------- */}
        <TradingHeader
          symbol={marketInfo.symbol}
          close={marketInfo.close}
          marketStatus={marketInfo.status}
          chartTS={marketInfo.timestamp}
        />

        {/* ---------------------------------------------------------------- */}
        {/* ERROR BANNER                                                     */}
        {/* ---------------------------------------------------------------- */}
        {marketInfo.error && <ErrorBanner message={marketInfo.error} />}

        {/* ---------------------------------------------------------------- */}
        {/* BODY                                                             */}
        {/* ---------------------------------------------------------------- */}
        {!marketInfo.isActive ? (
          <ConnectingScreen />
        ) : (
          <>
            {/* 1. DAY P&L SUMMARY STRIP */}
            <div style={{ marginBottom: "12px" }}>
              <DayPnLSummary
                dayPL={metrics.dayPL}
                realizedPL={metrics.realizedPL}
                unrealizedPL={metrics.unrealizedPL}
                activePositions={metrics.activePositions}
                closedTrades={metrics.closedTrades}
                winRate={metrics.winRate}
                avgHoldTime={metrics.avgHoldTime}
                medianHoldTime={metrics.medianHoldTime}
                bestTrade={metrics.bestTrade}
                worstTrade={metrics.worstTrade}
                totalFees={metrics.totalFees}
              />
            </div>

            {/* 2. POSITION LEDGER */}
            <div style={{ marginBottom: "12px" }}>
              <PositionLedger
                rawOrders={allOrders}
                ledgerView={ledgerData}
                proceedEvals={proceedEvals}
                onOptionClick={handleOptionClick}
              />
            </div>

            {/* 3. OPEN ORDERS */}
            <div style={{ marginBottom: "12px" }}>
              <Orders key={1} data={allOrders} />
            </div>

            {/* 4. BOT INTELLIGENCE PANEL */}
            <div style={{ marginBottom: "12px" }}>
              <BotIntelligencePanel
                liveBars={liveBars}
                activePositions={activePositions}
                closedPositions={closedPositions}
                currentPrice={currentPrice}
                metrics={metrics}
                onClosePosition={handleClosePosition}
              />
            </div>

            {/* 5. ACTIVE POSITIONS + CHART */}
            <Row className="g-3 mb-3">
              <Col xs={12} lg={3}>
                <div style={{
                  height: "615px",
                  backgroundColor: T.bg1,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}>
                  <div style={{ padding: "12px 14px 0", borderBottom: `1px solid ${T.border}`, paddingBottom: "8px" }}>
                    <Eyebrow color={T.amber}>Guiding Trade</Eyebrow>
                  </div>
                  <div style={{ height: "calc(100% - 41px)", overflow: "auto" }}>
                    <ActivePositions
                      positions={activePositions}
                      liveBars={liveBars}
                      currentPrice={currentPrice}
                      onClose={handleClosePosition}
                      onDetail={(position) => handlePositionSelect(position)}
                      onSelectPosition={(posId) => {
                        const pos = activePositions.find((p) => p.id === posId || p.order_id === posId);
                        if (pos) setSelectedPosition(pos);
                      }}
                      selectedPositionId={selectedPosition?.id || selectedPosition?.order_id}
                    />
                  </div>
                </div>
              </Col>

              <Col xs={12} lg={9}>
                <div style={{
                  backgroundColor: T.bg1,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}>
                  <div style={{
                    padding: "10px 14px",
                    borderBottom: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}>
                    <Eyebrow color={T.cyan}>Market Analysis</Eyebrow>
                    <span style={{ marginLeft: "4px" }}>
                      <Chip label={marketInfo.symbol || "—"} color={T.cyan} />
                    </span>
                  </div>
                  <div style={{ padding: "8px" }}>
                    {liveBars.length > 0 ? (
                      <MarketChart data={liveBars} selectedPosition={selectedPosition} />
                    ) : (
                      <div style={{
                        height: "620px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      }}>
                        <Orb color={T.cyan} size={10} />
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "12px", color: T.meta }}>
                          Loading chart data…
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* CLOSED POSITIONS below chart */}
                
                
              </Col>
            </Row>

            {/* 6. EVALUATION TIMELINE */}
            {liveBars.length > 0 && (
              <div style={{
                backgroundColor: T.bg1,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                overflow: "hidden",
                marginBottom: "12px",
              }}>
                <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>
                  <Eyebrow color={T.purple}>AI Evaluation Timeline</Eyebrow>
                </div>
                <EvaluationTimeline
                  predictions={liveBars}
                  onEvaluationSelect={handleEvaluationSelect}
                  selectedEvalId={selectedPosition?.id || selectedPosition?.a_id}
                  positions={positions}
                  onPositionSelect={handlePositionSelect}
                />
              </div>
            )}
          </>
        )}
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* MODALS                                                              */}
      {/* ------------------------------------------------------------------ */}
      <EvaluationDetailModal
        evaluation={selectedEvaluation}
        show={showEvalModal}
        onHide={() => setShowEvalModal(false)}
      />

      <PositionDetailModal
        position={selectedPosition}
        chartData={selectedPosition ? filterChartDataForPosition(liveBars, selectedPosition) : []}
        evaluations={selectedPosition ? filterEvaluationsForPosition(liveBars, selectedPosition) : []}
        show={showPositionModal}
        onHide={handlePositionModalClose}
        onViewFullStory={handleViewFullStory}
      />

      <OptionsStrategyPanel
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        position={selectedOption}
        matchedEval={selectedOptionEval}
      />
      
    </div>
  );
};

export default TradingCommand;